import React, { useState } from 'react';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { Button } from 'design-system';
import { Modal, ModalHeader, ModalBody } from 'design-system/Modal';

interface ExperimentTaskInfoModalProps {
  subtask: string;
}
const ExperimentTaskInfoModal: React.FC<ExperimentTaskInfoModalProps> = ({ subtask }) => {
  const [showModal, setShowModal] = useState(false);
  const [doneOptOut, setDoneOptOut] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [doneFullOptOut, setDoneFullOptOut] = useState(false);

  const performOptOut = async () => {
    await apiFetch(null, `internal/self_serve/tasker/opt_out`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subtask }),
    });

    setDoneOptOut(true);
  };

  const performFullOptOut = async () => {
    await apiFetch(null, `internal/self_serve/tasker/opt_out_permanent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    setDoneFullOptOut(true);
  };

  const close = () => {
    setShowModal(false);
  };

  const content = (
    <>
      <div style={{ padding: 12, paddingTop: 0 }}>
        <div style={{ fontWeight: 'bold' }}>What is an Experiment Task?</div>
        <div style={{}}>
          Experiment Tasks are special tasks that <b>pay more</b> than regular tasks. In addition to
          correctly annotating the task, we'd like to hear any feedback you have about how we can
          make the instructions better. Be sure to give feedback about the instructions, <b>not</b>{' '}
          any existing work on the task.
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 'bold' }}>Why did I get this task?</div>
        <div style={{}}>
          You were chosen for this task because of the <b>high quality</b> of your work on other
          projects. Good job! Keep up the good work to get more tasks like this and earn more money!
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 'bold' }}>Why wasn't there any training?</div>
        <div style={{}}>
          Experiment tasks are for very new projects that don't have training yet! That is why we
          need your help to make the training as good as possible.
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 'bold' }}>How do I write good feedback?</div>
        <div style={{}}>
          <a onClick={() => setShowInstructions(true)}>Learn how to write good feedback</a>
        </div>
      </div>
      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 'bold' }}>Don't want to work on this project?</div>
        <div style={{ paddingBottom: 12 }}>No problem, you can opt out below</div>
        {doneOptOut ? (
          <span>
            Done opting out! <a href="/tasks">Return to tasking</a>
          </span>
        ) : (
          <>
            <Button
              onClick={async () => {
                await performOptOut();
              }}
            >
              Opt out of project
            </Button>
          </>
        )}
      </div>
      <div style={{ padding: 12 }}>
        <div style={{ fontWeight: 'bold' }}>Don't want to work on experiment tasks at all?</div>
        <div style={{ paddingBottom: 12 }}>
          You can <b>permanently</b> opt out from all experiment tasks below
        </div>
        {doneFullOptOut ? (
          <span>
            Done opting out! <a href="/tasks">Return to tasking</a>
          </span>
        ) : (
          <>
            <Button
              onClick={async () => {
                await performFullOptOut();
              }}
            >
              Opt out of all experiment tasks (permanent)
            </Button>
          </>
        )}
      </div>
    </>
  );

  const instructionsContent = (
    <div style={{ maxHeight: 600, overflowY: 'scroll' }}>
      <div>
        If the Instructions are not clear enough and you don't fully understand the expected
        workflow for each task, you should mark the option “I have concerns about the task”.
        Instructions Feedback will have to include the missing areas so the customer can then
        improve their instructions. It is very important that Instructions are well explained and
        include examples of a correct task. If the Instructions are clear and you fully understand
        what to do in the task, please select “I confidently completed the task”. Well done tasks
        will then be used to train future taskers in this project, so please make sure that your
        task is as perfect as it can be.
      </div>
      <br />
      <div style={{ fontWeight: 500 }}>Examples of good feedback:</div>
      <ul>
        <li>
          The instructions do not include a step by step workflow. It would help a lot if the
          instructions also included image examples of correct vs incorrect annotations in order to
          achieve the project's expectations.{' '}
        </li>
        <li>
          The instructions include what to do in Red images, but there are Yellow images that are
          not included in the instructions. It would be helpful to add all of the potential Edge
          Cases that might appear so labelers know what to expect and how to act upon these cases.{' '}
        </li>
        <li>
          Instructions are clear and straightforward, but visual examples are needed for the minimum
          size annotations because far away objects become very blurry and difficult to identify
          correctly.{' '}
        </li>
        <li>
          The third section of the Instructions called “Label Attributes” are missing visual
          examples of how to correctly select attributes for “X” label and “Z” label.{' '}
        </li>
        <li>
          It is unclear if “X” label should include “Z” and “Y” objects. Apart from that, the
          instructions are very straightforward.{' '}
        </li>
        <li>
          The project's expectations are unclear because there is no description in the instructions
          of what the minimum size annotations should be. There are objects far away that are too
          blurry to identify as “X” or “Z” labels, so it would be very helpful if the instructions
          added more detail on what to do in these cases.{' '}
        </li>
      </ul>

      <br />
      <div style={{ fontWeight: 500 }}>Examples of bad feedback:</div>
      <ul>
        <li>Instructions are good and clear.</li>
        <li>I read everything and understood everything.</li>
        <li>I do not understand a section of the instructions.</li>
        <li>This task is shorter than what I expected. </li>
        <li>Image is too blurry.</li>
      </ul>
      <div>
        Make sure that the feedback always includes suggestions of what specifically can be improved
        when something is unclear.{' '}
      </div>
      <br />
      <a onClick={() => setShowInstructions(false)}>Go back</a>
    </div>
  );

  return (
    <>
      <Modal maxWidth={1200} isOpen={showModal} onRequestClose={close}>
        <ModalHeader onRequestClose={close}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* flask icon */}
            <svg
              width="36"
              height="36"
              viewBox="0 0 110 110"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="110" height="110" fill="#E5E5E5" />
              <rect width="810" height="2420" transform="translate(-120 -1500)" fill="white" />
              <path
                d="M55 110C85.3757 110 110 85.3757 110 55C110 24.6243 85.3757 0 55 0C24.6243 0 0 24.6243 0 55C0 85.3757 24.6243 110 55 110Z"
                fill="#035670"
              />
              <path
                d="M62.5 22.5H47.5C46.12 22.5 45 23.62 45 25C45 26.38 46.12 27.5 47.5 27.5H62.5C63.88 27.5 65 26.38 65 25C65 23.62 63.88 22.5 62.5 22.5Z"
                fill="#29CD9C"
              />
              <path d="M62.5 27.5H47.5V42.5H62.5V27.5Z" fill="#EDDEBF" />
              <path d="M62.5 27.5H47.5V30H62.5V27.5Z" fill="#A0D3F0" />
              <path
                d="M36.5203 87.5L62.5003 42.5H47.5003L29.0203 74.51C25.6853 80.285 29.8553 87.5 36.5203 87.5Z"
                fill="#29CD9C"
              />
              <path
                d="M73.48 87.5L47.5 42.5H62.5L80.98 74.51C84.315 80.285 80.145 87.5 73.48 87.5Z"
                fill="#A0D3F0"
              />
              <path d="M73.4805 87.5L55.0005 55.49L36.5205 87.5H73.4805Z" fill="#EDDEBF" />
            </svg>
            <span style={{ marginLeft: 12 }}>Experiment Tasks</span>
          </div>
        </ModalHeader>
        <div style={{ padding: 12 }}>{showInstructions ? instructionsContent : content}</div>
        <ModalBody></ModalBody>
      </Modal>
      <div style={{ fontSize: 16 }}>
        You got an experiment task! 🎉
        <a style={{ paddingLeft: 8 }} onClick={() => setShowModal(true)}>
          Learn More / Opt Out
        </a>
      </div>
      <div style={{ marginTop: 8, fontSize: 14, opacity: 0.5 }}>
        Experiment tasks pay 30-50% more than regular tasks. To continue getting tasks like these,
        complete the task with high quality and leave instruction feedback below. Be sure to give
        feedback about the instructions, <b>not</b> any existing work on the task.
      </div>
    </>
  );
};

export default ExperimentTaskInfoModal;
